//@flow

import React, { useEffect, useState } from "react";

import type { BruImageProps } from "@/components/BruImage/BruImage";
import BruImage from "@/components/BruImage/BruImage";

type HiddenBruImageProps = {|
    ...BruImageProps,
    alt?: string,
    onCurrentSrcChange: (src: string) => void,
|};

function getCurrentSource(image: HTMLImageElement): string {
    if ("currentSrc" in HTMLImageElement.prototype) {
        return image.currentSrc;
    }

    // Return `src` on browsers that do not support `currentSrc`.
    return image.src;
}

const useCurrentSrcState = (
    ref: { current: HTMLImageElement | null },
    onChange: (src: string) => void,
) => {
    const [currentSrc, setCurrentSrc] = useState<string | void>(undefined);

    useEffect(() => {
        const image = ref.current;
        if (image == null) return;

        function onLoad() {
            if (image) {
                const newSource = getCurrentSource(image);
                if (newSource !== currentSrc) {
                    setCurrentSrc(newSource);
                    onChange(newSource);
                }
            }
        }

        // Call onLoad directly if the image has already loaded.
        if (image.complete) {
            onLoad();
        }

        image.addEventListener("load", onLoad);
        return () => image.removeEventListener("load", onLoad);
    }, [ref, onChange, currentSrc, setCurrentSrc]);

    return [currentSrc];
};

const HiddenBruImage = (props: HiddenBruImageProps): React$Element<typeof BruImage> => {
    const ref = React.createRef<HTMLImageElement>();
    const withDefaults = {
        ...props,
        alt: props.alt || "",
        forwardRef: ref,
        style: {
            ...(props?.style || {}),
            position: "absolute",
            width: "0",
            height: "0",
        },
    };
    useCurrentSrcState(ref, props.onCurrentSrcChange);
    return (
        <BruImage
            forwardRef={withDefaults.forwardRef}
            alt={withDefaults.alt}
            image={withDefaults.image}
            className={withDefaults.className}
            style={withDefaults.style}
            loading={withDefaults.loading}
        />
    );
};

export default HiddenBruImage;
